/**
 * Feature E02-F04-T08 — "Add Information" links/files api-client + Query hooks.
 *
 * The Save-Information text/file save path already exists
 * (`project/projectSaveApi.ts` → `useSaveProjectChanges`, PATCH /projects/:id
 * multipart). This file covers the other two persistence paths the Add
 * Information block needs — immediate (not staged) mutations, 1:1 port of the
 * legacy `addProjectLink` / `deleteProjectLink` / `deleteProjectFile`:
 *
 *   - POST   /projects/:id/links        add_link      (T05)
 *   - DELETE /projects/:id/links/:index delete_link   (T05)
 *   - DELETE /projects/:id/files/:index delete_file   (T05)
 *
 * All three invalidate the project's detail card so the re-read reflects the
 * persisted `manual_context.links`/`.files` (DB = source of truth).
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { successAckSchema } from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

// ---------------------------------------------------------------------------
// add_link
// ---------------------------------------------------------------------------

export interface AddLinkVars {
  readonly projectId: number;
  readonly url: string;
}

/** POST /projects/:id/links — append a manual link (server scrapes title/content). */
export async function addProjectLink(vars: AddLinkVars): Promise<void> {
  const result = await apiClient.post<unknown>(
    `/projects/${vars.projectId}/links`,
    { url: vars.url },
    { command: 'add_link' },
  );
  successAckSchema.parse(unwrap(result));
}

export function useAddLink(): UseMutationResult<void, ApiResultError, AddLinkVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, AddLinkVars>({
    mutationFn: (vars) => addProjectLink(vars),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// refetch_link (PK-QA-5)
// ---------------------------------------------------------------------------

export interface RefetchLinkVars {
  readonly projectId: number;
  readonly linkIndex: number;
}

/**
 * PK-QA-5 — POST /projects/:id/links/:index/refetch: re-scrape the link in place
 * and refresh its `content` / `fetch_status` / `fetched_at` (the "Retry" action
 * behind a failed-scrape badge). A DEDICATED endpoint is used rather than
 * re-adding the URL through {@link addProjectLink}, because `addLink` has no
 * dedup (Links parity) and would append a DUPLICATE link on every retry.
 */
export async function refetchProjectLink(vars: RefetchLinkVars): Promise<void> {
  const result = await apiClient.post<unknown>(
    `/projects/${vars.projectId}/links/${vars.linkIndex}/refetch`,
    {},
    { command: 'refetch_link' },
  );
  successAckSchema.parse(unwrap(result));
}

export function useRefetchLink(): UseMutationResult<void, ApiResultError, RefetchLinkVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, RefetchLinkVars>({
    mutationFn: (vars) => refetchProjectLink(vars),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// delete_link
// ---------------------------------------------------------------------------

export interface DeleteLinkVars {
  readonly projectId: number;
  readonly linkIndex: number;
  /**
   * E02-F03-T08 — the URL of the row the user actually clicked. `linkIndex`
   * alone is a position in a shared jsonb array taken from a render that may
   * already be stale (another session added/removed a link meanwhile), which
   * would delete the WRONG row with a cheerful 200. Sending the URL makes the
   * server match by identity and no-op when the link is already gone.
   */
  readonly url?: string;
}

/**
 * DELETE /projects/:id/links/:index — remove a manual link. The index addresses
 * the row; `url` (when known) is the identity guard the server prefers over it.
 */
export async function deleteProjectLink(vars: DeleteLinkVars): Promise<void> {
  const result = await apiClient.delete<unknown>(
    `/projects/${vars.projectId}/links/${vars.linkIndex}`,
    { command: 'delete_link', ...(vars.url ? { query: { url: vars.url } } : {}) },
  );
  successAckSchema.parse(unwrap(result));
}

export function useDeleteLink(): UseMutationResult<void, ApiResultError, DeleteLinkVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, DeleteLinkVars>({
    mutationFn: (vars) => deleteProjectLink(vars),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// delete_file
// ---------------------------------------------------------------------------

export interface DeleteFileVars {
  readonly projectId: number;
  readonly fileIndex: number;
}

/** DELETE /projects/:id/files/:index — remove a saved file by position. */
export async function deleteProjectFile(vars: DeleteFileVars): Promise<void> {
  const result = await apiClient.delete<unknown>(
    `/projects/${vars.projectId}/files/${vars.fileIndex}`,
    { command: 'delete_file' },
  );
  successAckSchema.parse(unwrap(result));
}

export function useDeleteFile(): UseMutationResult<void, ApiResultError, DeleteFileVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, DeleteFileVars>({
    mutationFn: (vars) => deleteProjectFile(vars),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}
